/**
 * Origin Private File System (OPFS) storage for Videa Notes recordings.
 *
 * OPFS provides persistent, quota-managed storage that survives page refreshes,
 * enabling recovery of interrupted recordings.
 *
 * Directory Structure:
 * /videa-notes-recordings/
 *   /{sessionId}/
 *     metadata.json
 *     transcripts.json
 *     chunk-0.webm
 *     chunk-1.webm
 *     ...
 */

import { z } from 'zod';

import { createDebugLogger } from '@videa/utils';

const debug = createDebugLogger('OPFS');

// Helper for optional nullable string fields (accepts undefined, null, or string)
const optionalString = z.string().nullable().optional();

const recordingSessionSchema = z.object({
  id: z.string(),
  unifiedAppointmentId: optionalString,
  standalonePatientVisitId: optionalString,
  patientVideaId: optionalString,
  templateId: optionalString,
  promptId: optionalString,
  organizationId: optionalString,
  practiceId: optionalString,
  createdAt: z.number(),
  chunkCount: z.number(),
  status: z.enum([
    'recording',
    'pending-upload',
    'uploading',
    'completed',
    'failed',
  ]),
  lastTranscriptContext: optionalString,
  // Enhanced appointment metadata
  appointmentStart: optionalString,
  appointmentImagesAt: optionalString,
  estimatedVisitId: z.number().nullable().optional(),
  pmsAppointmentId: optionalString,
  providerId: optionalString,
  imagingPatientId: optionalString,
  pmsPatientId: optionalString,
  // External patient ID (ChartNum) for recovery
  externalPatientId: optionalString,
  // Patient name for recovery (needed for /ambient standalone notes)
  patientFirstName: optionalString,
  patientLastName: optionalString,
  // Synapse async upload - stored for recovery/restart
  ambientSessionId: optionalString,
});

export type RecordingSession = z.infer<typeof recordingSessionSchema>;

export type RecordingSessionMetadata = Omit<
  RecordingSession,
  'id' | 'createdAt' | 'chunkCount' | 'status'
>;

const transcriptEntrySchema = z.object({
  timestamp: z.number(),
  text: z.string(),
  interim: z.boolean(),
  chunkIndex: z.number().optional(),
  confidence: z.number().optional(),
});

export type TranscriptEntry = z.infer<typeof transcriptEntrySchema>;

const transcriptDataSchema = z.object({
  transcripts: z.array(transcriptEntrySchema),
  lastUpdated: z.number(),
  totalCharacters: z.number(),
});

export type TranscriptData = z.infer<typeof transcriptDataSchema>;

const ROOT_DIR_NAME = 'videa-notes-recordings';
const METADATA_FILE = 'metadata.json';
const TRANSCRIPTS_FILE = 'transcripts.json';

/**
 * Generate a unique session ID using crypto.randomUUID if available,
 * or fallback to timestamp + random suffix.
 */
function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * OPFS-based storage for Videa Notes audio recordings.
 *
 * Provides methods for creating, reading, and managing recording sessions
 * with automatic chunk-based persistence.
 *
 * Note: Uses a mutex to serialize operations because OPFS doesn't handle
 * concurrent access well, especially after page reloads.
 */
export class VideaNotesOPFS {
  private rootDirHandle: FileSystemDirectoryHandle | null = null;
  private operationMutex: Promise<void> = Promise.resolve();

  /**
   * Acquire the mutex and run an operation.
   * This ensures only one OPFS operation runs at a time to prevent
   * concurrent access issues that cause stale handle errors.
   */
  private async withMutex<T>(operation: () => Promise<T>): Promise<T> {
    // Chain this operation after the current one
    const currentMutex = this.operationMutex;
    let resolve: () => void;
    this.operationMutex = new Promise<void>((r) => {
      resolve = r;
    });

    try {
      // Wait for previous operation to complete
      await currentMutex;
      // Run this operation
      return await operation();
    } finally {
      // Release mutex for next operation
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- resolve is always assigned synchronously by the Promise constructor above
      resolve!();
    }
  }

  /**
   * Get or create the root directory for recordings.
   * Always fetches fresh handle to avoid stale reference issues.
   */
  private async getRootDir(): Promise<FileSystemDirectoryHandle> {
    // For OPFS, always get a fresh handle to avoid stale reference issues
    // OPFS handles don't support queryPermission() like user-selected files do
    // See: https://developer.mozilla.org/en-US/docs/Web/API/FileSystemHandle
    const opfsRoot = await navigator.storage.getDirectory();
    this.rootDirHandle = await opfsRoot.getDirectoryHandle(ROOT_DIR_NAME, {
      create: true,
    });
    debug.log('getRootDir: got root directory handle');
    return this.rootDirHandle;
  }

  /**
   * Clear the cached root directory handle.
   * Call this if operations are failing due to stale handles.
   */
  clearCache(): void {
    this.rootDirHandle = null;
  }

  /**
   * Get the directory handle for a specific session.
   */
  private async getSessionDir(
    sessionId: string,
    create = false,
  ): Promise<FileSystemDirectoryHandle> {
    const rootDir = await this.getRootDir();
    try {
      const sessionDir = await rootDir.getDirectoryHandle(sessionId, {
        create,
      });
      debug.log(`getSessionDir: got handle for ${sessionId}, create=${create}`);
      return sessionDir;
    } catch (error) {
      debug.log(
        `getSessionDir: FAILED for ${sessionId}, create=${create}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Read the metadata file for a session.
   */
  private async readMetadata(
    sessionDir: FileSystemDirectoryHandle,
  ): Promise<RecordingSession> {
    const fileHandle = await sessionDir.getFileHandle(METADATA_FILE);
    const file = await fileHandle.getFile();
    const text = await file.text();
    const parsed = recordingSessionSchema.safeParse(JSON.parse(text));
    if (!parsed.success) {
      throw new Error(`Invalid session metadata: ${parsed.error.message}`);
    }
    return parsed.data;
  }

  /**
   * Write the metadata file for a session.
   */
  private async writeMetadata(
    sessionDir: FileSystemDirectoryHandle,
    metadata: RecordingSession,
  ): Promise<void> {
    const fileHandle = await sessionDir.getFileHandle(METADATA_FILE, {
      create: true,
    });
    let writable: FileSystemWritableFileStream | null = null;
    try {
      writable = await fileHandle.createWritable();
      await writable.write(JSON.stringify(metadata, null, 2));
      await writable.close();
      writable = null; // Mark as successfully closed
    } catch (error) {
      // Ensure stream is closed/aborted even on error
      if (writable) {
        try {
          await writable.abort();
        } catch {
          // Ignore abort errors
        }
      }
      throw error;
    }
  }

  /**
   * Create a new recording session with the given metadata.
   * Returns the unique session ID.
   * Uses mutex to prevent concurrent access issues.
   */
  async createSession(metadata: RecordingSessionMetadata): Promise<string> {
    return this.withMutex(async () => {
      const sessionId = generateSessionId();
      const sessionDir = await this.getSessionDir(sessionId, true);

      const session: RecordingSession = {
        id: sessionId,
        ...metadata,
        createdAt: Date.now(),
        chunkCount: 0,
        status: 'recording',
      };

      await this.writeMetadata(sessionDir, session);
      return sessionId;
    });
  }

  /**
   * Write an audio chunk to the session.
   * Chunks are stored as chunk-{index}.webm files.
   * Handles stale handle errors by retrying with fresh handles.
   * Uses mutex to prevent concurrent access issues.
   *
   * @throws QuotaExceededError if storage is critically low (< 10MB available)
   */
  async writeChunk(
    sessionId: string,
    chunkIndex: number,
    blob: Blob,
  ): Promise<void> {
    // Check for sufficient storage before attempting write
    // This prevents silent failures when storage is full
    await checkStorageQuota();

    return this.withMutex(async () => {
      const writeWithRetry = async (isRetry = false): Promise<void> => {
        let writable: FileSystemWritableFileStream | null = null;
        try {
          // Use create: true in case the session dir needs to be re-acquired after page reload
          const sessionDir = await this.getSessionDir(sessionId, true);
          const chunkFileName = `chunk-${chunkIndex}.webm`;

          const fileHandle = await sessionDir.getFileHandle(chunkFileName, {
            create: true,
          });
          writable = await fileHandle.createWritable();
          await writable.write(blob);
          await writable.close();
          writable = null; // Mark as successfully closed

          // Update chunk count in metadata
          try {
            const metadata = await this.readMetadata(sessionDir);
            metadata.chunkCount = Math.max(metadata.chunkCount, chunkIndex + 1);
            await this.writeMetadata(sessionDir, metadata);
          } catch (metaError) {
            // Metadata update failed but chunk was written - log but don't fail
            // eslint-disable-next-line no-console
            debug.warn(
              'OPFS metadata update failed after chunk write:',
              metaError,
            );
          }
        } catch (error) {
          // Ensure stream is closed/aborted even on error
          if (writable) {
            try {
              await writable.abort();
            } catch {
              // Ignore abort errors
            }
          }
          // If we get a stale handle error and haven't retried, try once more
          if (!isRetry && this.isStaleHandleError(error)) {
            // eslint-disable-next-line no-console
            debug.warn('OPFS stale handle detected, retrying chunk write...');
            this.clearCache();
            return writeWithRetry(true);
          }
          throw error;
        }
      };

      await writeWithRetry();
    });
  }

  /**
   * Check if a session exists and is readable.
   */
  async sessionExists(sessionId: string): Promise<boolean> {
    try {
      const sessionDir = await this.getSessionDir(sessionId);
      await this.readMetadata(sessionDir);
      return true;
    } catch (error) {
      // Expected when session doesn't exist
      debug.log('sessionExists check failed:', error);
      return false;
    }
  }

  /**
   * Read a complete session including metadata and all chunks.
   * Chunks are returned in order as an array of Blobs.
   * Handles stale handle errors by retrying with fresh handles.
   * Uses mutex to prevent concurrent access issues.
   */
  async readSession(
    sessionId: string,
  ): Promise<{ metadata: RecordingSession; chunks: (Blob | null)[] }> {
    return this.withMutex(async () => {
      const readWithRetry = async (
        isRetry = false,
      ): Promise<{ metadata: RecordingSession; chunks: (Blob | null)[] }> => {
        try {
          debug.log(
            `readSession: getting session dir for ${sessionId}, isRetry=${isRetry}`,
          );

          // Don't use create: true here - session must exist
          const sessionDir = await this.getSessionDir(sessionId, false);
          debug.log('readSession: got session dir, reading metadata');

          const metadata = await this.readMetadata(sessionDir);
          debug.log(
            `readSession: metadata shows ${metadata.chunkCount} chunks`,
          );

          const chunks: (Blob | null)[] = [];
          for (let i = 0; i < metadata.chunkCount; i++) {
            const chunkFileName = `chunk-${i}.webm`;
            try {
              const fileHandle = await sessionDir.getFileHandle(chunkFileName);
              const file = await fileHandle.getFile();
              chunks.push(file);
            } catch {
              // Chunk file missing — likely deleted by the SW after upload.
              // Push null to preserve index alignment so callers can skip
              // already-uploaded chunks while still processing later ones.
              chunks.push(null);
            }
          }

          debug.log(`readSession: read ${chunks.length} chunks successfully`);
          return { metadata, chunks };
        } catch (error) {
          debug.log(
            `readSession failed for ${sessionId}, isRetry=${isRetry}:`,
            error,
          );

          if (!isRetry && this.isStaleHandleError(error)) {
            // eslint-disable-next-line no-console
            debug.warn('OPFS stale handle detected, retrying session read...');
            this.clearCache();
            return readWithRetry(true);
          }
          throw error;
        }
      };

      return readWithRetry();
    });
  }

  /**
   * Reconstruct the full audio blob from all chunks.
   * Returns a single Blob with the specified MIME type.
   * Note: readSession already uses mutex, so no need to wrap here.
   */
  async reconstructBlob(
    sessionId: string,
    mimeType = 'audio/webm',
  ): Promise<Blob> {
    const { chunks } = await this.readSession(sessionId);
    const presentChunks = chunks.filter((c): c is Blob => c !== null);
    return new Blob(presentChunks, { type: mimeType });
  }

  /**
   * Update the status of a session.
   * Handles stale handle errors by retrying with fresh handles.
   * Uses mutex to prevent concurrent access issues.
   */
  async updateSessionStatus(
    sessionId: string,
    status: RecordingSession['status'],
  ): Promise<void> {
    return this.withMutex(async () => {
      const updateWithRetry = async (isRetry = false): Promise<void> => {
        try {
          // Don't use create: true - session must exist to update status
          const sessionDir = await this.getSessionDir(sessionId, false);
          const metadata = await this.readMetadata(sessionDir);
          metadata.status = status;
          await this.writeMetadata(sessionDir, metadata);
        } catch (error) {
          if (!isRetry && this.isStaleHandleError(error)) {
            // eslint-disable-next-line no-console
            debug.warn('OPFS stale handle detected, retrying status update...');
            this.clearCache();
            return updateWithRetry(true);
          }
          throw error;
        }
      };

      await updateWithRetry();
    });
  }

  /**
   * Update the last transcript context for recovery UI.
   * Called periodically during recording to persist STT preview.
   * Uses mutex to prevent concurrent access issues.
   */
  async updateTranscriptContext(
    sessionId: string,
    context: string,
  ): Promise<void> {
    return this.withMutex(async () => {
      const updateWithRetry = async (isRetry = false): Promise<void> => {
        try {
          // Don't use create: true - session must exist to update transcript
          const sessionDir = await this.getSessionDir(sessionId, false);
          const metadata = await this.readMetadata(sessionDir);
          metadata.lastTranscriptContext = context;
          await this.writeMetadata(sessionDir, metadata);
        } catch (error) {
          if (!isRetry && this.isStaleHandleError(error)) {
            // eslint-disable-next-line no-console
            debug.warn(
              'OPFS stale handle detected, retrying transcript update...',
            );
            this.clearCache();
            return updateWithRetry(true);
          }
          throw error;
        }
      };

      await updateWithRetry();
    });
  }

  /**
   * Update the Ambient session ID for a recording session.
   * Called after successfully creating an Ambient session, so recovery
   * can resume uploading to the same session.
   * Uses mutex to prevent concurrent access issues.
   */
  async updateAmbientSessionId(
    sessionId: string,
    ambientSessionId: string,
  ): Promise<void> {
    return this.withMutex(async () => {
      const updateWithRetry = async (isRetry = false): Promise<void> => {
        try {
          // Don't use create: true - session must exist to update
          const sessionDir = await this.getSessionDir(sessionId, false);
          const metadata = await this.readMetadata(sessionDir);
          metadata.ambientSessionId = ambientSessionId;
          await this.writeMetadata(sessionDir, metadata);
          debug.log(
            `Updated ambientSessionId for ${sessionId}: ${ambientSessionId}`,
          );
        } catch (error) {
          if (!isRetry && this.isStaleHandleError(error)) {
            // eslint-disable-next-line no-console
            debug.warn(
              'OPFS stale handle detected, retrying ambientSessionId update...',
            );
            this.clearCache();
            return updateWithRetry(true);
          }
          throw error;
        }
      };

      await updateWithRetry();
    });
  }

  /**
   * Update arbitrary metadata fields on a session.
   * Only provided fields are overwritten; others are preserved.
   */
  async updateSessionFields(
    sessionId: string,
    fields: Partial<RecordingSession>,
  ): Promise<void> {
    return this.withMutex(async () => {
      const updateWithRetry = async (isRetry = false): Promise<void> => {
        try {
          const sessionDir = await this.getSessionDir(sessionId, false);
          const metadata = await this.readMetadata(sessionDir);
          Object.assign(metadata, fields);
          await this.writeMetadata(sessionDir, metadata);
        } catch (error) {
          if (!isRetry && this.isStaleHandleError(error)) {
            debug.warn(
              'OPFS stale handle detected, retrying updateSessionFields...',
            );
            this.clearCache();
            return updateWithRetry(true);
          }
          throw error;
        }
      };

      await updateWithRetry();
    });
  }

  /**
   * Check if an error is a stale handle error that can be retried.
   */
  private isStaleHandleError(error: unknown): boolean {
    if (error instanceof DOMException) {
      return (
        error.name === 'NoModificationAllowedError' ||
        error.name === 'NotFoundError' ||
        error.name === 'InvalidStateError'
      );
    }
    return false;
  }

  /**
   * List all recording sessions.
   */
  async listSessions(): Promise<RecordingSession[]> {
    const rootDir = await this.getRootDir();
    const sessions: RecordingSession[] = [];

    // Use type assertion for the async iterator since OPFS types may be incomplete
    const entries = rootDir as unknown as AsyncIterable<
      [string, FileSystemHandle]
    >;
    for await (const [name, entry] of entries) {
      if (entry.kind === 'directory') {
        try {
          const sessionDir = await rootDir.getDirectoryHandle(name);
          const metadata = await this.readMetadata(sessionDir);
          sessions.push(metadata);
        } catch (error) {
          // Skip sessions with invalid/missing metadata
          debug.log(`skipping invalid session ${name}:`, error);
        }
      }
    }

    // Sort by creation time, newest first
    return sessions.sort((a, b) => b.createdAt - a.createdAt);
  }

  /**
   * Get summary stats of all OPFS sessions for analytics/debugging.
   * Returns counts by status and age, useful for Mixpanel tracking.
   */
  async getSessionStats(): Promise<{
    totalSessions: number;
    byStatus: Record<string, number>;
    byAge: { lastHour: number; lastDay: number; older: number };
    totalChunks: number;
    sessionsWithAmbientId: number;
    oldestSessionAge: number | null;
  }> {
    const sessions = await this.listSessions();
    const now = Date.now();
    const ONE_HOUR = 60 * 60 * 1000;
    const ONE_DAY = 24 * ONE_HOUR;

    const stats = {
      totalSessions: sessions.length,
      byStatus: {} as Record<string, number>,
      byAge: { lastHour: 0, lastDay: 0, older: 0 },
      totalChunks: 0,
      sessionsWithAmbientId: 0,
      oldestSessionAge: null as number | null,
    };

    for (const session of sessions) {
      // Count by status
      const status = session.status ?? 'unknown';
      stats.byStatus[status] = (stats.byStatus[status] ?? 0) + 1;

      // Count by age
      const age = now - session.createdAt;
      if (age < ONE_HOUR) {
        stats.byAge.lastHour++;
      } else if (age < ONE_DAY) {
        stats.byAge.lastDay++;
      } else {
        stats.byAge.older++;
      }

      // Track oldest
      if (stats.oldestSessionAge === null || age > stats.oldestSessionAge) {
        stats.oldestSessionAge = age;
      }

      // Count chunks
      stats.totalChunks += session.chunkCount ?? 0;

      // Count sessions with ambient ID (should be cleaned up)
      if (session.ambientSessionId) {
        stats.sessionsWithAmbientId++;
      }
    }

    return stats;
  }

  /**
   * Delete a recording session and all its chunks.
   * Uses mutex to prevent concurrent access issues.
   * Includes retry logic for stale handle recovery.
   */
  async deleteSession(sessionId: string): Promise<void> {
    const deleteWithRetry = async (isRetry = false): Promise<void> => {
      const rootDir = await this.getRootDir();
      try {
        await rootDir.removeEntry(sessionId, { recursive: true });
      } catch (error) {
        // Check if this is a stale handle error that we should retry
        if (
          !isRetry &&
          error instanceof DOMException &&
          (error.name === 'NotFoundError' ||
            error.name === 'NoModificationAllowedError' ||
            error.name === 'InvalidStateError')
        ) {
          debug.log(
            'Stale handle on deleteSession, clearing cache and retrying...',
          );
          this.clearCache();
          return deleteWithRetry(true);
        }
        // After retry or for other errors, just log and move on
        // Session not existing is expected in some cleanup flows
        debug.log(`deleteSession ${sessionId} failed:`, error);
      }
    };

    return this.withMutex(() => deleteWithRetry());
  }

  /**
   * Delete a single chunk from a session.
   * Called after successful upload to free up storage.
   */
  async deleteChunk(sessionId: string, chunkIndex: number): Promise<void> {
    return this.withMutex(async () => {
      const deleteWithRetry = async (isRetry = false): Promise<void> => {
        try {
          const sessionDir = await this.getSessionDir(sessionId, false);
          const chunkFileName = `chunk-${chunkIndex}.webm`;
          await sessionDir.removeEntry(chunkFileName);
          debug.log(`Deleted chunk ${chunkFileName} from session ${sessionId}`);
        } catch (error) {
          // Check if this is a stale handle error that we should retry
          if (!isRetry && this.isStaleHandleError(error)) {
            debug.log(
              'Stale handle on deleteChunk, clearing cache and retrying...',
            );
            this.clearCache();
            return deleteWithRetry(true);
          }
          // NotFoundError is expected if chunk was already deleted
          if (error instanceof DOMException && error.name === 'NotFoundError') {
            debug.log(
              `Chunk ${chunkIndex} already deleted from session ${sessionId}`,
            );
            return;
          }
          throw error;
        }
      };

      await deleteWithRetry();
    });
  }

  /**
   * Clean up stale sessions older than maxAgeMs.
   * Useful for garbage collection of abandoned recordings.
   */
  async cleanupStale(maxAgeMs: number): Promise<void> {
    const sessions = await this.listSessions();
    const now = Date.now();

    for (const session of sessions) {
      // Only clean up sessions that are not actively uploading
      if (
        session.status !== 'uploading' &&
        now - session.createdAt > maxAgeMs
      ) {
        await this.deleteSession(session.id);
      }
    }
  }

  /**
   * Find sessions that need recovery (interrupted recordings).
   * Returns sessions that are still marked as 'recording' but were created
   * before the current page load.
   */
  async findRecoverableSessions(): Promise<RecordingSession[]> {
    const sessions = await this.listSessions();
    const pageLoadTime = performance.timeOrigin;

    return sessions.filter(
      (session) =>
        session.status === 'recording' && session.createdAt < pageLoadTime,
    );
  }

  /**
   * Read transcript data from OPFS for a session.
   */
  private async readTranscripts(
    sessionDir: FileSystemDirectoryHandle,
  ): Promise<TranscriptData> {
    try {
      const transcriptFile = await sessionDir.getFileHandle(TRANSCRIPTS_FILE);
      const file = await transcriptFile.getFile();
      const text = await file.text();
      const parsed = transcriptDataSchema.safeParse(JSON.parse(text));
      if (parsed.success) {
        return parsed.data;
      }
      // Invalid format - return empty
      return { transcripts: [], lastUpdated: Date.now(), totalCharacters: 0 };
    } catch {
      // Return empty transcript data if file doesn't exist
      return { transcripts: [], lastUpdated: Date.now(), totalCharacters: 0 };
    }
  }

  /**
   * Write transcript data to OPFS.
   */
  private async writeTranscripts(
    sessionDir: FileSystemDirectoryHandle,
    transcriptData: TranscriptData,
  ): Promise<void> {
    const transcriptFile = await sessionDir.getFileHandle(TRANSCRIPTS_FILE, {
      create: true,
    });
    let writable: FileSystemWritableFileStream | null = null;
    try {
      writable = await transcriptFile.createWritable();
      await writable.write(JSON.stringify(transcriptData, null, 2));
      await writable.close();
      writable = null; // Mark as successfully closed
    } catch (error) {
      // Ensure stream is closed/aborted even on error
      if (writable) {
        try {
          await writable.abort();
        } catch {
          // Ignore abort errors
        }
      }
      throw error;
    }
  }

  /**
   * Append a new transcript entry to a session.
   * Called during recording when new STT results are available.
   */
  async appendTranscript(
    sessionId: string,
    text: string,
    options: {
      interim?: boolean;
      chunkIndex?: number;
      confidence?: number;
    } = {},
  ): Promise<void> {
    debug.log(
      `appendTranscript called: sessionId=${sessionId}, text="${text}", options=`,
      options,
    );
    if (!text.trim()) {
      debug.log('appendTranscript: skipping empty text');
      return; // Skip empty transcripts
    }

    return this.withMutex(async () => {
      const appendWithRetry = async (isRetry = false): Promise<void> => {
        try {
          const sessionDir = await this.getSessionDir(sessionId, false);
          const transcriptData = await this.readTranscripts(sessionDir);

          const entry: TranscriptEntry = {
            timestamp: Date.now(),
            text: text.trim(),
            interim: options.interim ?? false,
            chunkIndex: options.chunkIndex,
            confidence: options.confidence,
          };

          transcriptData.transcripts.push(entry);
          transcriptData.lastUpdated = Date.now();
          transcriptData.totalCharacters = transcriptData.transcripts
            .filter((t) => !t.interim)
            .reduce((sum, t) => sum + t.text.length, 0);

          await this.writeTranscripts(sessionDir, transcriptData);
        } catch (error) {
          if (!isRetry && this.isStaleHandleError(error)) {
            debug.warn(
              'OPFS stale handle detected, retrying transcript append...',
            );
            this.clearCache();
            return appendWithRetry(true);
          }
          throw error;
        }
      };

      await appendWithRetry();
    });
  }

  /**
   * Get all transcripts for a session.
   */
  async getTranscripts(sessionId: string): Promise<TranscriptData> {
    return this.withMutex(async () => {
      try {
        const sessionDir = await this.getSessionDir(sessionId, false);
        return await this.readTranscripts(sessionDir);
      } catch (error) {
        if (this.isStaleHandleError(error)) {
          this.clearCache();
          // Retry once
          const sessionDir = await this.getSessionDir(sessionId, false);
          return await this.readTranscripts(sessionDir);
        }
        throw error;
      }
    });
  }

  /**
   * Clear all transcripts for a session.
   */
  async clearTranscripts(sessionId: string): Promise<void> {
    return this.withMutex(async () => {
      const clearWithRetry = async (isRetry = false): Promise<void> => {
        try {
          const sessionDir = await this.getSessionDir(sessionId, false);
          try {
            await sessionDir.removeEntry(TRANSCRIPTS_FILE);
          } catch (error) {
            // Ignore if file doesn't exist
            if (
              error instanceof DOMException &&
              error.name !== 'NotFoundError'
            ) {
              throw error;
            }
          }
        } catch (error) {
          if (!isRetry && this.isStaleHandleError(error)) {
            // eslint-disable-next-line no-console
            debug.warn(
              'OPFS stale handle detected, retrying clear transcripts...',
            );
            this.clearCache();
            return clearWithRetry(true);
          }
          throw error;
        }
      };

      await clearWithRetry();
    });
  }

  /**
   * Check if OPFS is available in the current browser.
   */
  static isAvailable(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      'storage' in navigator &&
      'getDirectory' in navigator.storage
    );
  }
}

// Singleton key for fallback storage
const OPFS_SINGLETON_KEY = '__videa_notes_opfs__';

/**
 * Safely get Vite's HMR data object if available.
 * Returns undefined in production builds, Jest, or non-Vite environments.
 *
 * Note: We use eval to access import.meta.hot to avoid Jest parse errors.
 * Jest uses CommonJS and fails at parse time on `import.meta` syntax.
 *
 * IMPORTANT: This module should NOT be imported directly into Service Workers.
 * Use '@repo/videa-notes/sw' which provides a minimal SW-safe OPFS implementation.
 * Zod's JIT compilation and this eval are both blocked by SW CSP.
 */
function getViteHmrData(): Record<string, unknown> | undefined {
  try {
    // Check if we're in an ESM context with Vite HMR
    // Use indirect eval to avoid Jest parse errors with import.meta
    // eslint-disable-next-line no-eval
    const hotData = eval(
      'typeof import.meta !== "undefined" && import.meta.hot?.data',
    );
    return hotData || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Create or retrieve the OPFS singleton, preserving it across Vite HMR.
 *
 * In development with Vite, uses `import.meta.hot.data` to preserve the
 * instance across Hot Module Replacement. Falls back to window storage
 * for production builds or environments without Vite HMR.
 *
 * Without this, HMR would create new instances that lose cached handles,
 * causing operations to fail with NotFoundError even when sessions exist.
 */
function createOpfsSingleton(): VideaNotesOPFS {
  // Try Vite HMR data first (development)
  const hmrData = getViteHmrData();
  if (hmrData) {
    const existing = hmrData.opfs as VideaNotesOPFS | undefined;
    if (existing instanceof VideaNotesOPFS) {
      debug.log('Reusing instance from Vite HMR data');
      return existing;
    }
    const instance = new VideaNotesOPFS();
    hmrData.opfs = instance;
    debug.log('Created instance, stored in Vite HMR data');
    return instance;
  }

  // Fallback: use window storage (production or non-Vite)
  if (typeof window !== 'undefined') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const win = window as any;
    const existing = win[OPFS_SINGLETON_KEY];
    if (existing instanceof VideaNotesOPFS) {
      return existing;
    }
    const instance = new VideaNotesOPFS();
    win[OPFS_SINGLETON_KEY] = instance;
    return instance;
  }

  // Server-side: create instance (won't be used for actual OPFS operations)
  return new VideaNotesOPFS();
}

// Default singleton instance (preserved across Vite HMR and page lifecycle)
export const opfs = createOpfsSingleton();

// =============================================================================
// QUOTA MONITORING
// =============================================================================

/**
 * Minimum available storage (in bytes) required before writing a chunk.
 * 10MB headroom ensures we don't fill storage completely, which could
 * cause cascading failures or browser issues.
 */
const MIN_AVAILABLE_STORAGE_BYTES = 10 * 1024 * 1024; // 10MB

/**
 * Custom error class for quota exceeded scenarios.
 * Allows callers to differentiate quota errors from other failures.
 */
export class QuotaExceededError extends Error {
  constructor(
    message: string,
    public readonly usage: number,
    public readonly quota: number,
  ) {
    super(message);
    this.name = 'QuotaExceededError';
  }
}

/**
 * Storage estimate result from the Storage Manager API.
 */
export interface StorageEstimate {
  /** Current storage usage in bytes */
  usage: number;
  /** Total storage quota in bytes */
  quota: number;
  /** Available storage remaining in bytes */
  available: number;
  /** Percentage of quota currently used (0-100) */
  percentUsed: number;
}

/**
 * Get an estimate of current storage usage and quota.
 *
 * Uses the Storage Manager API to query browser storage estimates.
 * Note: Values are estimates and may not reflect exact OPFS usage,
 * but are useful for detecting low storage conditions.
 *
 * @returns Storage estimate or null if API is unavailable
 */
export async function getStorageEstimate(): Promise<StorageEstimate | null> {
  try {
    if (typeof navigator === 'undefined' || !navigator.storage?.estimate) {
      return null;
    }

    const estimate = await navigator.storage.estimate();
    const usage = estimate.usage ?? 0;
    const quota = estimate.quota ?? 0;
    const available = Math.max(0, quota - usage);
    const percentUsed = quota > 0 ? (usage / quota) * 100 : 0;

    return {
      usage,
      quota,
      available,
      percentUsed,
    };
  } catch (error) {
    debug.warn('Failed to get storage estimate:', error);
    return null;
  }
}

/**
 * Check if there is sufficient storage available for a write operation.
 *
 * @param requiredBytes - Optional minimum bytes needed (defaults to MIN_AVAILABLE_STORAGE_BYTES)
 * @throws QuotaExceededError if storage is below threshold
 */
export async function checkStorageQuota(
  requiredBytes: number = MIN_AVAILABLE_STORAGE_BYTES,
): Promise<void> {
  const estimate = await getStorageEstimate();

  if (estimate && estimate.available < requiredBytes) {
    throw new QuotaExceededError(
      `Insufficient storage: ${Math.round(
        estimate.available / 1024 / 1024,
      )}MB available, ` +
        `${Math.round(requiredBytes / 1024 / 1024)}MB required. ` +
        `Storage is ${estimate.percentUsed.toFixed(1)}% full.`,
      estimate.usage,
      estimate.quota,
    );
  }
}
